import clsx from "clsx";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { MdOutlineAccountCircle } from "react-icons/md";
import useMobile from "src/hooks/useMobile";
import ShinyCard from "../BankCard/ShinyCard";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import SmartImage from "../SmartImage/SmartImage";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";

export enum EContactCardType {
  SENT = "SENT",
  RECEIVED = "RECEIVED",
  ACCEPTED = "ACCEPTED",
  SEND = "SEND",
}

interface IContactCard {
  name: string;
  customAnimationDirection?: "x" | "y" | null;
  index: number;
  type: EContactCardType;
  picture?: string | null;
  subText?: string;
  actions?: IButton[];
  onClick?: () => void | null;
  vertical?: boolean;
}

const ContactCard: React.FC<IContactCard> = ({
  customAnimationDirection = null,
  index,
  type,
  name,
  picture,
  subText,
  actions = [],
  onClick,
  vertical = false,
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { isMobile } = useMobile();

  const animationDirection = useMemo(() => {
    return customAnimationDirection
      ? customAnimationDirection
      : isMobile
        ? "y"
        : "x";
  }, [isMobile]);

  return (
    <motion.div
      className={clsx([
        "flex",
        "w-full",
        "group",
        // "bg-gray-50/50 dark:bg-gray-900/50",
        "hover:bg-gray-100",
        "hover:dark:bg-gray-900",
        // "transition-opacity",
        "p-2",
        "md:p-2",
        "rounded",
        "gap-2",
        "items-center",
        "border",
        "border-gray-50",
        "dark:border-gray-900/50",
        {
          "cursor-pointer": onClick,
        },
        vertical && ["justify-center", "flex-col"],
      ])}
      {...(enableAnimations && {
        initial: {
          opacity: 0,
          [animationDirection]: index % 2 === 0 ? -100 : 100,
        },
        animate: { opacity: 1, [animationDirection]: 0 },
        transition: {
          duration: 0.2,
          ease: "easeOut",
          // delay: index * 0.1,
        },
      })}
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      <div
        className={clsx([
          "rounded-full",
          "overflow-hidden",
          "shadow",
          "inline-flex",
          "items-center",
          "justify-center",
          "relative",
          // "bg-checkers",
          "bg-white",

          vertical
            ? [
                "!h-[70px]",
                "!w-[70px]",
                "!min-h-[70px]",
                "!min-w-[70px]",
                "md:!h-[70px]",
                "md:!w-[70px]",
                "md:!min-h-[70px]",
                "md:!min-w-[70px]",
              ]
            : [
                "!h-[50px]",
                "!w-[50px]",
                "!min-h-[50px]",
                "!min-w-[50px]",
                "md:!h-[50px]",
                "md:!w-[50px]",
                "md:!min-h-[50px]",
                "md:!min-w-[50px]",
              ],
        ])}
      >
        <ShinyCard>
          {picture ? (
            <SmartImage
              src={picture}
              className={clsx(["!w-full", "!h-full"])}
              alt={name}
            />
          ) : (
            <div
              className={clsx([
                "flex",
                "h-full",
                "w-full",
                "bg-white",
                "items-center",
                "justify-center",
              ])}
            >
              <MdOutlineAccountCircle
                size={isMobile ? ICON_SIZES.MD : ICON_SIZES.LG}
                className={clsx([
                  "dark:text-purple-400",
                  "text-red-400",
                  "bg-white",
                  "dark:bg-gray-100",
                  "rounded-full",
                ])}
              />
            </div>
          )}
        </ShinyCard>
      </div>
      <div className={clsx(["flex", "flex-col", "truncate", "flex-grow"])}>
        <div
          className={clsx([
            "md:text-sm",
            "text-xs",
            "truncate",
            "font-medium",
            {
              "text-left": !vertical,
              "text-center": vertical,
            },
          ])}
        >
          {name}
        </div>
        {subText && (
          <div
            className={clsx([
              "text-xs",
              "truncate",
              {
                "text-left": !vertical,
                "text-center": vertical,
              },
            ])}
          >
            {subText}
          </div>
        )}
      </div>
      {actions?.length > 0 && <ButtonGroup buttons={actions} />}
    </motion.div>
  );
};

export default ContactCard;
